import { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getMapCenter } from './shopsData';
import markerUrl from './assets/mmjmarker.png';
import './DashboardShopsMap.css';

const MARKER_NATURAL_W = 175;
const MARKER_NATURAL_H = 226;
const MARKER_DISPLAY_H = 44;
const ICON_W = Math.round((MARKER_NATURAL_W / MARKER_NATURAL_H) * MARKER_DISPLAY_H);
const ICON_H = MARKER_DISPLAY_H;

const shopIcon = L.icon({
  iconUrl: markerUrl,
  iconSize: [ICON_W, ICON_H],
  iconAnchor: [ICON_W / 2, ICON_H],
  popupAnchor: [0, -ICON_H + 4],
  className: 'dashboard-shop-marker-icon',
});

function validShops(shops) {
  return (shops || []).filter(
    (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && (p.lat !== 0 || p.lng !== 0)
  );
}

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions.length) return;
    const b = L.latLngBounds(positions);
    map.fitBounds(b, { padding: [52, 52], maxZoom: 12 });
  }, [map, positions]);
  return null;
}

function MapStatusCopy({ loading, points, total }) {
  if (loading) {
    return {
      title: 'Mağaza Haritası',
      sub: 'Konumlar yükleniyor…',
      badge: null,
    };
  }
  if (points.length) {
    return {
      title: 'Mağaza Haritası',
      sub: `${points.length} mağaza haritada`,
      badge: `${points.length}`,
    };
  }
  if (total > 0) {
    return {
      title: 'Mağaza Haritası',
      sub: 'Konum eklenmemiş mağazalar var',
      badge: '0',
    };
  }
  return {
    title: 'Mağaza Haritası',
    sub: 'Henüz mağaza eklenmedi',
    badge: null,
  };
}

export default function DashboardShopsMap({
  shops = [],
  loading = false,
  premium = false,
  onManageShops,
  onShopSelect,
}) {
  const points = useMemo(() => validShops(shops), [shops]);
  const center = useMemo(() => getMapCenter(points.length ? points : shops), [points, shops]);
  const boundsPositions = useMemo(() => points.map((p) => [p.lat, p.lng]), [points]);
  const status = MapStatusCopy({ loading, points, total: shops.length });

  const mapBlock = (
    <div className="dashboard-map-wrap">
      {loading ? (
        <div className="dashboard-map-loading" aria-live="polite">
          <div className="dashboard-map-loading-shimmer" aria-hidden />
          <p>Harita hazırlanıyor</p>
        </div>
      ) : null}
      {!loading && !points.length ? (
        <div className="dashboard-map-empty" aria-live="polite">
          <div className="dashboard-map-empty-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </div>
          <p className="dashboard-map-empty-title">Gösterilecek konum yok</p>
          <p className="dashboard-map-empty-sub">
            Mağazalar sayfasından enlem ve boylam ekleyin; konumlar burada görünür.
          </p>
          {onManageShops ? (
            <button type="button" className="dashboard-map-empty-btn" onClick={onManageShops}>
              Mağazaları Yönet
            </button>
          ) : null}
        </div>
      ) : null}
      <MapContainer
        center={center}
        zoom={points.length ? 10 : 11}
        scrollWheelZoom
        attributionControl={false}
        zoomControl={false}
        className={`dashboard-map ${!points.length && !loading ? 'dashboard-map--hidden' : ''}`}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
        />
        {boundsPositions.length > 1 ? <FitBounds positions={boundsPositions} /> : null}
        {points.map((shop) => (
          <Marker
            key={shop.id}
            position={[shop.lat, shop.lng]}
            icon={shopIcon}
            eventHandlers={onShopSelect ? {
              click: () => onShopSelect(shop),
            } : undefined}
          >
            <Popup className="dashboard-map-popup">
              <div className="dashboard-map-popup-inner">
                <strong>{shop.name}</strong>
                {shop.description?.trim() ? (
                  <p className="dashboard-map-popup-desc">{shop.description.trim()}</p>
                ) : null}
                {onShopSelect ? (
                  <button
                    type="button"
                    className="dashboard-map-popup-btn"
                    onClick={() => onShopSelect(shop)}
                  >
                    Mağazayı aç
                  </button>
                ) : null}
                {shop.mapslink?.trim() ? (
                  <a
                    className="dashboard-map-popup-link"
                    href={shop.mapslink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Haritada Aç
                  </a>
                ) : null}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );

  if (premium) {
    return (
      <section
        className="dashboard-map-section dashboard-map-section--premium"
        aria-label="Mağaza haritası"
      >
        <div className="dashboard-map-shell">
          {mapBlock}
        </div>
      </section>
    );
  }

  return (
    <section className="dashboard-map-section" aria-label="Mağaza haritası">
      <div className="dashboard-map-header">
        <h2>{status.title}</h2>
        <p className="dashboard-map-sub">{status.sub}</p>
      </div>
      {mapBlock}
    </section>
  );
}
